import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './vouching-manual.scss';

/**
 * `/readme/vouching` manual content (TRIL-422), built on the TRIL-424 framework.
 * Ported from the review visual (docs/manual/vouching.visual.html) into in-app
 * design tokens; source copy is docs/manual/vouching.md. Major blocks carry ids
 * so a help icon can deep-link to a specific part (e.g. /readme/vouching#revoke).
 *
 * The numeric mechanics in the prose (60-day window, 30-day taper half-life,
 * 5 concurrent max) mirror the server source of truth in
 * `server/src/helpers/vouching/vouchMath.ts`; there is no shared client constant
 * yet, so keep these in sync if that changes (a shared constant is a possible
 * follow-up, deferred as YAGNI for a single prose page).
 */
export default function VouchingManual() {
  const { hash } = useLocation();

  // Deep-link support: /readme/vouching#<id> scrolls that block into view.
  // (This page's blocks are always-visible, so there is nothing to expand —
  // only to scroll to. The framework's <ManualSection> handles the expandable
  // case for pages that use it.)
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    el?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [hash]);

  return (
    <div className="VOUCH__MANUAL">
      <p className="MANUAL__LEAD">
        A vouch is a way for an established member to give someone a temporary
        boost in <strong>reach</strong> — how often their stories show up in
        other people&apos;s feeds. It&apos;s a leg-up, not a shortcut: a window
        of extra visibility, not someone else&apos;s reputation handed over.
      </p>

      {/* 1 · roles */}
      <section id="roles" className="VOUCH__MANUAL__BLOCK">
        <h2>Who&apos;s involved</h2>
        <p className="VOUCH__MANUAL__SUB">
          Two people, two very different stakes.
        </p>
        <div className="VOUCH__MANUAL__GRID2">
          <div className="VOUCH__MANUAL__CARD">
            <span className="VOUCH__PILL VOUCH__PILL--active">Voucher</span>
            <p>
              Spends a little of their standing to back you. It{' '}
              <b>costs nothing upfront</b> — their own score and reach are
              untouched — but they become a <b>co-signer</b> who has staked
              credits on the outcome.
            </p>
          </div>
          <div className="VOUCH__MANUAL__CARD">
            <span className="VOUCH__PILL VOUCH__PILL--success">Recipient</span>
            <p>
              Gets the reach boost for <b>60 days</b>. Nothing to accept — it
              simply lifts how often your stories are shown. What you do with
              the window is up to you.
            </p>
          </div>
        </div>
      </section>

      {/* 2 · lifecycle — the chart */}
      <section id="lifecycle" className="VOUCH__MANUAL__BLOCK">
        <h2>The lifecycle of a vouch</h2>
        <p className="VOUCH__MANUAL__SUB">
          Created → boosted → resolved, over 60 days.
        </p>
        <div className="VOUCH__TL">
          <div className="VOUCH__TL__STEP">
            <span className="VOUCH__TL__DOT">▶</span>
            <span className="VOUCH__TL__WHEN">Day 0</span>
            <h3>A vouch is created</h3>
            <p>
              The voucher stakes some credits and your reach starts getting a
              lift. No cost to your reputation on either side.
            </p>
          </div>
          <div className="VOUCH__TL__STEP">
            <span className="VOUCH__TL__DOT">~</span>
            <span className="VOUCH__TL__WHEN">Days 0–60</span>
            <h3>The boost tapers, then cuts off</h3>
            <p>
              It&apos;s strongest right away and{' '}
              <b>halves about every 30 days</b> — roughly half gone by the
              one-month mark, and still around a quarter of the original at day
              60. At day 60 the window closes and it <b>cuts off entirely.</b> A
              running start, not a permanent perch.
            </p>
            {/* Decorative only. Real boost ≈ 2^(−ageDays/30), so it tapers to
                ~25% by day 60, then cliffs to 0 at the window boundary — the
                bars end at ~25%, not near zero, to avoid implying a smooth
                glide to nothing. Source of truth: server vouchMath.ts. */}
            <div className="VOUCH__TAPER" aria-hidden="true">
              {[100, 87, 76, 66, 57, 50, 44, 38, 33, 29, 25].map((h, i) => (
                <span
                  // biome-ignore lint/suspicious/noArrayIndexKey: static decorative bars
                  key={i}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
            <div className="VOUCH__TAPER__LABEL">
              <span>Day 0 — full boost</span>
              <span>Day 60 — ~25%, then cutoff</span>
            </div>
          </div>
          <div className="VOUCH__TL__STEP VOUCH__TL__STEP--resolve">
            <span className="VOUCH__TL__DOT">◆</span>
            <span className="VOUCH__TL__WHEN">Day 60</span>
            <h3>The vouch resolves</h3>
            <p>
              The window closes and the vouch settles into exactly one of three
              outcomes — judged by what actually happened to the recipient. See
              below.
            </p>
          </div>
        </div>
      </section>

      {/* 3 · revoke */}
      <section id="revoke" className="VOUCH__MANUAL__BLOCK">
        <h2>Revoking — and what &ldquo;winding down&rdquo; means</h2>
        <p className="VOUCH__MANUAL__SUB">
          A voucher can revoke a vouch anytime. This is the part people most
          often ask about, so it&apos;s worth being precise.
        </p>
        <div className="VOUCH__REVOKE">
          <div className="VOUCH__REVOKE__HEAD">
            <span className="VOUCH__PILL VOUCH__PILL--active">Revoke</span>
            <span className="VOUCH__REVOKE__BADGE">
              roster shows: &ldquo;Revoked — winding down&rdquo;
            </span>
          </div>
          <ul>
            <li>
              The recipient&apos;s reach boost <b>ends right away.</b>
            </li>
            <li>
              The voucher&apos;s staked credits{' '}
              <b>do not come back immediately.</b> They stay committed and
              at-risk until that vouch&apos;s original 60-day window closes —{' '}
              <b>
                measured from when it was created, not from when it was revoked.
              </b>{' '}
              So the remaining wait is whatever is left of the 60 days (at most
              60, usually less).
            </li>
            <li>
              At that point it resolves like any other vouch — a <b>Return</b>,
              or a <b>Burn</b> if the recipient was moderated during the window.
            </li>
          </ul>
          <p className="VOUCH__WHY">
            <b>Why it can&apos;t just refund instantly:</b> if it did, a voucher
            could back someone, collect the upside, then bail the moment that
            person looked risky — dodging the accountability entirely. Keeping
            the credits committed through the original window is what keeps a
            vouch meaningful.{' '}
            <b>Revoke ends the boost early, but not the commitment.</b>
          </p>
          <p className="VOUCH__WHY VOUCH__WHY--reward">
            <b>You can still re-vouch.</b> Revoking doesn&apos;t lock you out —
            you can vouch for the same person again anytime you have available
            credits, even while an earlier revoked vouch is still winding down.
          </p>
        </div>
      </section>

      {/* 4 · outcomes */}
      <section id="outcomes" className="VOUCH__MANUAL__BLOCK">
        <h2>The three outcomes</h2>
        <p className="VOUCH__MANUAL__SUB">
          Settled by what the recipient actually did — whether their{' '}
          <b>own earned reputation</b> rose (not likes or engagement, so it
          can&apos;t be faked by buying attention), and whether they were
          moderated.
        </p>
        <div className="VOUCH__BRANCHES">
          <div className="VOUCH__BRANCH VOUCH__BRANCH--return">
            <span className="VOUCH__PILL VOUCH__PILL--return">Return</span>
            <h3>An honest bet</h3>
            <p className="VOUCH__BRANCH__COND">
              The window ended and nothing bad happened
            </p>
            <ul>
              <li>
                Staked credits <b>come back</b>
              </li>
              <li>
                No reward — and <b>no penalty</b>
              </li>
              <li>The default, low-drama outcome</li>
            </ul>
          </div>
          <div className="VOUCH__BRANCH VOUCH__BRANCH--success">
            <span className="VOUCH__PILL VOUCH__PILL--success">Success</span>
            <h3>Reach became standing</h3>
            <p className="VOUCH__BRANCH__COND">
              The recipient&apos;s earned reputation genuinely rose
            </p>
            <ul>
              <li>
                Staked credits <b>come back</b>
              </li>
              <li>
                The voucher earns a <b>successful mentorship</b>
              </li>
              <li>Which grows their own community standing over time</li>
            </ul>
          </div>
          <div className="VOUCH__BRANCH VOUCH__BRANCH--burn">
            <span className="VOUCH__PILL VOUCH__PILL--burn">Burn</span>
            <h3>The co-signer cost</h3>
            <p className="VOUCH__BRANCH__COND">
              The recipient was <b>moderated</b> in the window (a story removed
              or sent back to rewrite)
            </p>
            <ul>
              <li>
                Staked credits are <b>lost permanently</b>
              </li>
              <li>
                The exception, <b>not</b> the default
              </li>
              <li>Someone simply not taking off is a Return, never a burn</li>
            </ul>
          </div>
        </div>
        <div className="VOUCH__NOTE">
          <span className="VOUCH__NOTE__MK">note</span>
          <p>
            <b>Burn only happens if the recipient is moderated.</b> Backing
            someone who just doesn&apos;t catch on costs you nothing beyond
            having your credits tied up for the window.
          </p>
        </div>
      </section>

      {/* 5 · good to know */}
      <section id="good-to-know" className="VOUCH__MANUAL__BLOCK">
        <h2>Good to know</h2>
        <div className="VOUCH__FACTS">
          <div className="VOUCH__FACT">
            <h3>It costs nothing upfront</h3>
            <p>
              Your own reputation score and your own reach are never touched.
              You lend from a separate credit budget — the surplus standing
              you&apos;ve earned. The credits you stake are{' '}
              <b>committed for the window</b> (tied up, not spent) and come back
              when the vouch resolves — unless it burns.
            </p>
          </div>
          <div className="VOUCH__FACT">
            <h3>The boost is capped</h3>
            <p>
              There&apos;s a limit on how much any one voucher can lift you, and
              a total cap across everyone. A group can&apos;t stack unlimited
              reach onto one account.
            </p>
          </div>
          <div className="VOUCH__FACT">
            <h3>One active vouch per person</h3>
            <p>
              You can&apos;t stack two live vouches on the same person — manage
              the existing one instead. You can hold up to five active vouches
              at once.
            </p>
          </div>
          <div className="VOUCH__FACT">
            <h3>Keep publishing</h3>
            <p>
              The best thing a recipient can do with the window is post. If your
              earned reputation rises during it, the vouch resolves as a
              Success.
            </p>
          </div>
        </div>
      </section>

      {/* coming soon — TRIL-395 foreshadow (misuse → reputation) */}
      <p className="VOUCH__COMING">
        <b>Coming soon:</b> repeatedly backing people who go on to be moderated
        will also weigh on your own reputation — an added check on misuse,
        beyond the per-vouch burn.
      </p>

      {/* short version */}
      <div className="VOUCH__TLDR">
        <h2>The short version</h2>
        <ul>
          <li>
            A vouch is <b>borrowed reach for 60 days</b>, tapering to nothing —
            a chance to be seen, not a permanent boost.
          </li>
          <li>
            Vouching <b>costs nothing upfront</b>, but the voucher{' '}
            <b>stakes credits</b> as a co-signer.
          </li>
          <li>
            It settles as <b>Return</b> (credits back, no reward),{' '}
            <b>Success</b> (credits back plus a reward, because the person
            earned real standing), or <b>Burn</b> (credits lost, because the
            person was moderated).
          </li>
          <li>
            <b>
              Revoking stops the boost immediately, but the staked credits stay
              committed until the original 60-day window ends
            </b>{' '}
            — so revoke can&apos;t dodge the risk. You can re-vouch anytime.
          </li>
        </ul>
      </div>
    </div>
  );
}
